<template>
  <div class="pc-plan-switcher-sm-container">
    <pc-tooltip :value="canSwitchPlan ? 'Switch plan' : 'Selected plan'">
      <v-select
        class="pc-plan-switcher"
        :background-color="$colors.penChecksBlueGray"
        :color="$colors.white"
        :id="id"
        item-text="displayName"
        item-value="id"
        :items="plans"
        :menu-props="{ contentClass: 'pc-plan-switcher-menu' }"
        :disabled="disabled || !canSwitchPlan"
        dense
        solo
        flat
        dark
        hide-details
        v-model="selectedPlanId"
        @change="onUserChange"
        :data-id="'58b8e000950d4213da6d9fad3904a7a1'"
      >
        <template v-slot:prepend-item v-if="upsertNliraPlansEnabled">
          <div class="pc-plan-switcher-header">
            <pc-button
              class="pc-plan-add-btn"
              outlined
              block
              :color="$colors.penChecksTeal"
              data-id="28057281-4425-4c1a-b5d6-f128d9512deb"
              @click.stop="$emit('add-plan')"
            >
              Add plan
              <pc-icon
                class="ml-2 mr-0 icon-button"
                light
                name="pc-add"
                :size="14"
              ></pc-icon>
            </pc-button>
          </div>
        </template>

        <template v-slot:append>
          <pc-icon
            class="pc-plan-switcher-chevron"
            :size="12"
            name="pc-scroll-down"
          ></pc-icon>
        </template>

        <template v-slot:prepend-inner>
          <div class="pc-plan-switcher-avatar">
            <v-avatar color="white" size="32">
              <span class="pc-plan-switcher-letter">{{
                selectedPlanFirstLetter
              }}</span>
            </v-avatar>
          </div>
        </template>

        <template v-slot:item="{ item }">
          <div class="pc-plan-switcher-item-container pl-2">
            <pc-tooltip avoid-overflow :value="item.displayName">
              <span class="pc-plan-switcher-item-text">
                {{ item.displayName }}
              </span>
            </pc-tooltip>
            <span class="ml-1">
              <span
                v-if="upsertNliraPlansEnabled"
                class="pc-plan-switcher-edit-container pl-2 pr-2"
                @click.stop="$emit('edit-plan', item)"
              >
                <pc-icon
                  class="pc-plan-switcher-icon"
                  name="pc-edit"
                  size="12"
                ></pc-icon>
              </span>
            </span>
          </div>
        </template>

        <template v-if="small" v-slot:selection></template>
      </v-select>
    </pc-tooltip>
  </div>
</template>

<script>
import PcTooltip from "../PcTooltip.vue";
import Plan from "@/shared/lib/client-sdk/models/plan";
import getFeatureFlag from "@/shared/lib/getFeatureFlag";

export default {
  components: { PcTooltip },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    value: {
      default: null,
    },
    small: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: undefined,
    },
    id: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    plans: [],
    loading: false,
    error: null,
    internalSelectedId: null,
    persistTimer: null,
    isHydratingChange: false,
    hydrating: true,
    lastPersistedUuid: null,
    savingSelection: false,
    userInitiatedSwitch: false,
    upsertNliraPlansEnabled: false,
  }),
  watch: {
    accountId: {
      immediate: true,
      handler() {
        this.fetchPlans();
      },
    },
    selectedPlanId(newVal) {
      if (!this.canSwitchPlan) return;
      if (!newVal || newVal === this.lastPersistedUuid) return;
      this.schedulePersistSelectedPlan(newVal);
    },
  },
  methods: {
    onUserChange(val) {
      if (this.isHydratingChange) return;
      this.userInitiatedSwitch = true;
    },
    fetchAccountSelectedPlanUuid() {
      Plan.getSelectedPlanOnAccount(this.accountId, (uuid, err) => {
        if (err) {
          console.error("Error fetching selected plan:", err);
          return;
        }
        if (uuid) {
          this.selectedPlanId = uuid; // display correct plan on load
        }
      });
    },
    fetchPlans: function () {
      if (!this.accountId) return;
      this.loading = true;
      this.error = null;

      Plan.fetchForAccount(
        this.accountId,
        function (items, err) {
          this.loading = false;

          if (err) {
            this.error = err;
            this.plans = [];
            return;
          }

          // sort by displayName to ensure a consistent experience
          this.plans = (items || []).slice().sort(function (a, b) {
            return (a.displayName || "").localeCompare(b.displayName || "");
          });

          const fromParentModel = this.value != null ? this.value : null; // v-model
          const fromVuex =
            this.$store?.state?.masterAccount?.selectedPlanId || null;

          const exists = (uuid) =>
            uuid && this.plans.some((p) => p.id === uuid);
          this.hydrating = true;
          this.isHydratingChange = true;

          Plan.getSelectedPlanOnAccount(this.accountId, (dbUuid, dbErr) => {
            if (!dbErr && exists(dbUuid)) {
              // Already saved: record it as persisted so the watcher doesn't save it again on every load,
              // and so a failed switch has something to revert to.
              this.lastPersistedUuid = dbUuid;
              this.selectedPlanId = dbUuid;
              return this.$nextTick(() => {
                this.hydrating = false;
                this.isHydratingChange = false;
              });
            }

            const fromParentModel = this.value != null ? this.value : null;
            if (exists(fromParentModel)) {
              this.selectedPlanId = fromParentModel;
              return this.$nextTick(() => {
                this.hydrating = false;
                this.isHydratingChange = false;
              });
            }

            const fromVuex =
              this.$store?.state?.masterAccount?.selectedPlanId || null;
            if (exists(fromVuex)) {
              this.selectedPlanId = fromVuex;
              return this.$nextTick(() => {
                this.hydrating = false;
                this.isHydratingChange = false;
              });
            }

            this.internalSelectedId = this.plans.length
              ? this.plans[0].id
              : null;
            return this.$nextTick(() => {
              this.hydrating = false;
              this.isHydratingChange = false;
            });
          });
        }.bind(this)
      );
    },
    schedulePersistSelectedPlan(selectedPlanUuid) {
      if (this.persistTimer) clearTimeout(this.persistTimer);
      this.persistTimer = setTimeout(() => {
        this.persistSelectedPlan(selectedPlanUuid);
      }, 400);
    },
    persistSelectedPlan(selectedPlanUuid) {
      if (!this.accountId || !selectedPlanUuid) return;
      this.savingSelection = true;

      Plan.setSelectedPlanOnAccount(
        this.accountId,
        selectedPlanUuid,
        (ok, err) => {
          this.savingSelection = false;
          if (ok) {
            this.lastPersistedUuid = selectedPlanUuid;
            if (this.userInitiatedSwitch) {
              this.userInitiatedSwitch = false; // reset for next time
              const targetPath = `/accounts/${this.accountId}`;
              if (this.$route.path !== targetPath) {
                window.location.assign(targetPath);
              } else {
                window.location.href = `${targetPath}?_r=${Date.now()}`;
              }
            }
            return;
          }

          // Persistence failed: revert and notify
          const previous = this.lastPersistedUuid;
          if (previous && previous !== this.selectedPlanId) {
            // prevent triggering another persist while reverting
            const wasHydrating = this.hydrating;
            this.hydrating = true;
            this.isHydratingChange = true;
            this.selectedPlanId = previous;
            this.$nextTick(() => {
              this.hydrating = wasHydrating;
              this.isHydratingChange = false;
            });
          }

          this.$snackbar.show({
            text: err || "Could not save selected plan.",
            color: this.$colors.penChecksRed,
          });
        }
      );
    },
    onPlanSelected(planId) {
      if (!planId) return;

      // Keep store's masterAccount.selectedPlanId in sync
      if (this.$store?.state?.masterAccount) {
        this.$store.commit("setMasterAccountSelectedPlanId", planId);
      }
    },
  },
  computed: {
    selectedPlanId: {
      get() {
        return this.value != null ? this.value : this.internalSelectedId;
      },
      set(value) {
        if (this.value != null) this.$emit("input", value);
        else this.internalSelectedId = value;
        this.onPlanSelected(value);
      },
    },
    selectedPlanFirstLetter: function () {
      const plan = this.plans.find((x) => x.id === this.selectedPlanId);
      const name = plan?.displayName || "";
      return name ? name.charAt(0).toUpperCase() : "";
    },
    // Mirrors SetAdvisorSelectedPlan's gate. Without it the switcher is read-only and never saves on the
    // user's behalf -- a save could only be refused.
    canSwitchPlan: function () {
      const user = this.$store?.state?.authenticatedUser;
      return !!(user && user.can("writeAccounts") && user.can("readPlans"));
    },
  },

  mounted: async function () {
    try {
      const result = await getFeatureFlag("upsertNliraPlans");
      this.upsertNliraPlansEnabled = result;
    } catch (error) {
      console.error("Error getting upsertNliraPlans feature flag:", error);
    }
  },
};
</script>

<style lang="scss">
.pc-plan-switcher-menu .v-list {
  padding: 16px 8px !important;

  .v-list-item {
    padding: 0px !important;
    border-radius: 4px;
  }
}

.icon-button {
  height: 20px !important;
  width: 20px !important;
}
.pc-plan-switcher.v-text-field--outlined,
.v-text-field--solo {
  border-radius: 0px !important;
}

.pc-plan-switcher-letter {
  color: $penChecksNavyBlue;
}

.pc-plan-switcher-menu.v-menu__content {
  border-radius: 0px !important;
  width: 256px !important;
  margin-top: 48px;
  top: 0px !important;
  left: 0px !important;
  max-height: 470px !important;
}

.pc-plan-switcher-menu .v-list {
  border-radius: 0px !important;
}
.pc-plan-switcher-avatar {
  padding: 8px 7px 8px 0px !important;
}

.pc-plan-switcher-container {
  width: 256px !important;
}

.pc-plan-switcher .v-select__selection {
  font-size: clamp(14px, 16px, 16px) !important;
  text-overflow: ellipsis !important;
}

.pc-plan-switcher-icon {
  color: $penChecksTeal !important;
}

.pc-plan-switcher-item-container {
  display: flex;
  justify-content: space-between;
  width: 100% !important;
}

.v-application .pc-plan-switcher-menu .primary--text {
  color: transparent !important;
  opacity: 1 !important;
  background: #ebf3f5 !important;
}

.pc-plan-switcher .v-select__selection {
  max-width: 156px !important;
}

.pc-plan-switcher-item-text {
  color: $penChecksDarkGray !important;
  overflow: hidden !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
  color: $penChecksDarkGray !important;
  max-width: 200px;
  display: block;
}

.pc-plan-switcher-chevron {
  margin-right: 4px !important;
}

.pc-plan-switcher-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  padding: 0px 8px;
  margin-bottom: 16px;
}

.pc-plan-add-btn {
  width: 100%;
}

.pc-plan-add-btn ::v-deep .v-btn {
  height: 44px !important;
  border-radius: 6px !important;
  padding: 0 16px !important;
}

.pc-plan-add-btn ::v-deep .v-btn__content {
  width: 100%;
  justify-content: center !important;
  text-align: center;
}

.pc-plan-add-icon {
  font-size: 16px;
  line-height: 1;
}
</style>
